import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../contexts/LanguageContext';

const PolicyDialog = ({ visible, onHide, hasScrolledToBottom, onScrollChange, onPolicyAccept }) => {
    const { th } = useLanguage();

    const handleClose = () => {
        if (!hasScrolledToBottom) return;
        onPolicyAccept();
        onHide();
    };

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header={th('technicalSupport.technicalPolicyTitle')}
            style={{ width: '70vw', maxWidth: '800px' }}
            modal
            maximizable
            closable={hasScrolledToBottom}
            closeOnEscape={hasScrolledToBottom}
            dismissableMask={false}
        >
            <div
                className="border-1 border-300 border-round p-4"
                style={{
                    height: '60vh',
                    overflowY: 'auto',
                    backgroundColor: '#f8f9fa'
                }}
                onScroll={(e) => {
                    const element = e.target;
                    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
                    if (isAtBottom && !hasScrolledToBottom) {
                        onScrollChange(true);
                    }
                }}
            >
                <div className="text-left py-4">
                    {/* Header */}
                    <div className="text-center mb-4 pb-3 border-bottom-1 border-300">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="text-600 text-sm">Date: 04.04.2025</span>
                            <span className="text-600 text-sm">Rev: v01</span>
                        </div>
                        <h2 className="text-900 font-bold mt-2 mb-0">After Sales Services Technical Support Request Policy</h2>
                    </div>

                    {/* Introduction */}
                    <div className="mb-4">
                        <p className="text-700 line-height-3 mb-3">
                            <strong>Dear Business Partners,</strong>
                        </p>
                        <p className="text-700 line-height-3 mb-3">
                            In line with KARSAN&apos;s growing global market share and increasing vehicle fleet, we are implementing the Technical Support Request Policy to manage, prioritize, and maximize the efficiency of the Technical Support
                            Requests we receive from you.
                        </p>
                        <p className="text-700 line-height-3 mb-3">
                            The aim of this policy is to continuously improve customer satisfaction by providing technical support in the fastest and most efficient manner. We believe that with your contributions, we can make this process more
                            systematic and effective, thereby strengthening our service quality together.
                        </p>
                        <p className="text-700 line-height-3 mb-3">Below, you will find information regarding the scope and implementation details of this policy.</p>
                    </div>

                    <div className="border-top-1 border-300 pt-3 mt-3 mb-4"></div>

                    {/* Section 1 */}
                    <div className="mb-4">
                        <h3 className="text-900 font-bold text-lg mb-3">1. FIELD TECHNICAL SUPPORT SERVICE REQUEST</h3>
                        <p className="text-700 line-height-3 mb-3">The Field Technical Support Team, operating under KARSAN After-Sales Services, provides both on-site and remote technical support to our authorized service centers.</p>
                        <p className="text-700 line-height-3 mb-3">Authorized service centers must meet the following prerequisites and submit their requests in writing to request on-site support from KARSAN Technical Support Personnel:</p>

                        <h4 className="text-900 font-semibold text-md mt-4 mb-2">1.1. Prerequisites for Field Technical Support Requests</h4>
                        <p className="text-700 line-height-3 mb-2">• The following steps must have been fully executed, and the issue must remain unresolved:</p>
                        <ul className="text-700 line-height-3 mb-3 ml-4" style={{ listStyleType: 'none', paddingLeft: '1rem' }}>
                            <li className="mb-2">
                                o The service technician must have intervened using up-to-date and accurate diagnostic equipment (All KARSAN authorized service centers are required to have up-to-date diagnostic equipment. Please contact your KARSAN
                                regional manager or spare parts representative if your diagnostic equipment is outdated or missing.)
                            </li>
                            <li className="mb-2">o The service technician must have used KARSAN technical documentation.</li>
                            <li className="mb-2">o The service technician must have intervened on-site.</li>
                            <li className="mb-2">o The service technician must have completed the minimum competency training.</li>
                            <li className="mb-2">o The service technician must have followed the instructions provided by the KARSAN Technical Support Engineer.</li>
                        </ul>
                        <p className="text-700 line-height-3 mb-3">• After fulfilling the above conditions, the Service Manager or Dealer Representative must contact the KARSAN Technical Support Team to request on-site intervention.</p>

                        <h4 className="text-900 font-semibold text-md mt-4 mb-2">1.2. Situations Where Field Technical Support Service Fees Apply</h4>
                        <p className="text-700 line-height-3 mb-2">
                            If the KARSAN Technical Support Team identifies any of the following situations during their on-site visit, travel expenses will be invoiced to the requesting service center/dealer upon mutual agreement:
                        </p>
                        <ul className="text-700 line-height-3 mb-3 ml-4">
                            <li className="mb-2">Issues arising from incomplete or incorrect interventions by the service center.</li>
                            <li className="mb-2">User-related issues.</li>
                            <li className="mb-2">Non-compliance with Long-Term Vehicle Storage Conditions.</li>
                            <li className="mb-2">Improper Management of Periodic Maintenance Process or Missing Records.</li>
                            <li className="mb-2">Issues caused by service bulletins that were previously published but not implemented by the service center or dealer.</li>
                            <li className="mb-2">Issues resulting from interventions performed by unqualified personnel.</li>
                            <li className="mb-2">Problems caused by modifications (e.g., ITS, ticket machines, etc.) added to the vehicle after production.</li>
                            <li className="mb-2">Failure to comply with written or verbal technical advice provided by the KARSAN Technical Support Team.</li>
                            <li className="mb-2">The service center does not provide roadside assistance.</li>
                            <li className="mb-2">The vehicle requiring technical support is out of warranty.</li>
                            <li className="mb-2">Last-minute cancellation of the scheduled workday or the vehicle being in use.</li>
                            <li className="mb-2">Necessary equipment and workspace (e.g., lift, pit, air conditioning leak detection device, etc.) are not being prepared on time.</li>
                            <li className="mb-2">
                                The authorized service center or dealer requesting technical support from KARSAN due to lack of technical personnel caused by seasonal workload, annual leave, or illness, to ensure uninterrupted customer service.
                            </li>
                        </ul>

                        <h4 className="text-900 font-semibold text-md mt-4 mb-2">1.3. Scope of Technical Support Service Fees</h4>
                        <p className="text-700 line-height-3 mb-2">Field Technical Support Services include the following items:</p>
                        <ul className="text-700 line-height-3 mb-3 ml-4">
                            <li className="mb-2">Travel costs</li>
                            <li className="mb-2">Accommodation</li>
                            <li className="mb-2">Labor (man-hours). This fee is at the same rate as the warranty hours in the relevant country.</li>
                            <li className="mb-2">All other expenses related to the intervention.</li>
                        </ul>
                    </div>

                    <div className="border-top-1 border-300 pt-3 mt-3 mb-4"></div>

                    {/* Section 2 */}
                    <div className="mb-4">
                        <h3 className="text-900 font-bold text-lg mb-3">2. REMOTE TECHNICAL SUPPORT SERVICE REQUEST</h3>
                        <p className="text-700 line-height-3 mb-2">Authorized service centers or dealers must meet the following conditions to request remote technical support:</p>
                        <ul className="text-700 line-height-3 mb-3 ml-4">
                            <li className="mb-2">Customer complaints, possible causes, and previously performed actions must be reported to KARSAN in advance.</li>
                            <li className="mb-2">The service technician must contact the KARSAN representative at least two (2) days before the customer visit.</li>
                            <li className="mb-2">All up-to-date diagnostic equipment must be available and in working condition.</li>
                            <li className="mb-2">The service technician must be competent and authorized.</li>
                        </ul>

                        <h4 className="text-900 font-semibold text-md mt-4 mb-2">2.1. Remote Technical Support Service Fees</h4>
                        <ul className="text-700 line-height-3 mb-3 ml-4">
                            <li className="mb-2">For vehicles under warranty, KARSAN remote technical support is free of charge.</li>
                            <li className="mb-2">For vehicles out of warranty, remote technical support is invoiced at man-hour rates (This fee is at the same rate as the warranty hours in the relevant country.).</li>
                        </ul>
                    </div>

                    {/* Conclusion */}
                    <div className="mt-4 pt-3 border-top-1 border-300">
                        <p className="text-700 line-height-3 mb-3">This policy has been implemented to ensure that technical support services are carried out more efficiently and systematically.</p>
                        <p className="text-700 font-semibold mt-4 mb-0">KARSAN Export After-Sales Services Department</p>
                    </div>
                </div>
            </div>

            <div className="flex justify-content-end mt-3">
                <Button label={th('common.close')} icon="pi pi-times" onClick={handleClose} className="p-button-text" disabled={!hasScrolledToBottom} />
            </div>
        </Dialog>
    );
};

export default PolicyDialog;
